<script lang="ts">
	// UI
	import {
		DefaultButton,
		Cancelabled,
		Selectabled,
		AdaptiveTextInput,
		InlineButton,
	} from "@hitit/new_ui/src";
	import DeleteIcon from "@hitit/hub/src/assets/Icons/Delete.png";
	import EditIcon from "@hitit/hub/src/assets/Icons/Edit.png";
	import SaveIcon from "@hitit/hub/src/assets/Icons/Save.png";
	// Utils
	import { t } from "@hitit/hub/src/stores";
	import { createEventDispatcher } from "svelte";
	import { getKeyInTranslation, sortableAction } from "../../utils";

	// Types
	import type { SortableEvent } from "sortablejs";
	import type { TranslationDTO } from "../../class/TranslationDTO";
	// Props
	export let placeholder = "";
	export let selectedCollectionUid = "";
	export let editmode = false;
	export let multipleSelectMode = false;
	export let editable = true;
	export let sortDisable = true;
	export let collections: {
		uid: string;
		id: string;
		translationDTO: TranslationDTO[];
		childrenLength: number;
	}[] = [];

	const dispatch = createEventDispatcher();
	//Testing now
	const multipleSelectModeEvent = () => dispatch("MULTIPLE_SELECT_MODE_CHANGE");
	const newCollectionEvent = (name: string) => {
		const empty = name === "";
		error = empty;
		if (!empty) return dispatch("NEW_COLLECTION", name);
	};
	const textClickEvent = (uid: string) => {
		selectedCollectionUid = uid;
		dispatch("CLICK_COLLECTION_TEXT", uid);
	};
	const multipleEditButtonClickEvent = () => dispatch("MULTIPLE_EDIT", "");
	const multipleDeleteButtonClickEvent = () => dispatch("MULTIPLE_DELETE", "");
	const editChange = () => {
		if (multipleSelectMode) {
			return;
		}

		dispatch("EDIT_CHANGE");
	};
	const upEvent = (categoryIndex: number) => {
		dispatch("INPUT_HARDWARE_UP_EVENT", { categoryIndex });
	};

	$: sortableOption = {
		group: {
			name: "category",
			put: true,
			pull: false,
		},
		disabled: sortDisable,
		delay: 150,
		animation: 150,
		swapThreshold: 0.65,
		forceFallback: true,
		ghostClass: "sortable-ghost",
		dragClass: "sortable-drag",
		draggable: ".-collection",
		filter: "#new-collection",
		onSort,
		onStart,
		onEnd,
	};

	let inputText = "";
	let error = false;
	// functions
	function onSort(event: SortableEvent) {
		const { newIndex, oldIndex } = event;
		dispatch("UPDATED_COLLECTION_DISPLAY_SORT", { newIndex, oldIndex });
	}
	function onStart() {
		document.body.classList.add("dragging");
	}
	function onEnd() {
		document.body.classList.remove("dragging");
	}

</script>

<div class="container">
	{#if editable}
		<div id="action" class:editmode class:multipleSelectMode>
			<div id="multipleselect">
				<DefaultButton
					fitmode
					background={multipleSelectMode ? '#bcbfc3' : '#6d6f76'}
					on:click={multipleSelectModeEvent}>
					<div id="inner">
						<div class="icon">
							{#if multipleSelectMode}
								<Cancelabled />
							{:else}
								<Selectabled />
							{/if}
						</div>
						<div class="text">
							{#if multipleSelectMode}
								{$t('global.cancel')}
							{:else}{$t('global.select')}{/if}
						</div>
					</div>
				</DefaultButton>
			</div>
			<div id="functions">
				<InlineButton on:click={multipleEditButtonClickEvent}>
					<img id="multiple-edit" src={EditIcon} alt={'Edit'} />
				</InlineButton>
				<InlineButton on:click={multipleDeleteButtonClickEvent}>
					<img id="multiple-delete" src={DeleteIcon} alt={'Delte'} />
				</InlineButton>
			</div>
			<InlineButton on:click={editChange} -edit-button>
				<div id="edit-change">
					<span>{editmode ? $t('global.ok') : $t('global.edit')}</span>
					<img
						src={editmode ? SaveIcon : EditIcon}
						alt={editmode ? 'save' : 'edit'} />
				</div>
			</InlineButton>
		</div>
	{/if}
	<div id="collections" use:sortableAction={sortableOption}>
		{#each collections as collection, index (collection.uid)}
			<InlineButton
				-collection={true}
				on:touchend={() => upEvent(index)}
				on:mouseup={() => upEvent(index)}
				on:click={() => textClickEvent(collection.uid)}>
				<p
					class="text"
					class:selected={selectedCollectionUid === collection.uid}>
					{getKeyInTranslation(collection.translationDTO, 'name', 'ko')}
					({collection.childrenLength})
				</p>
			</InlineButton>
		{/each}
		{#if editmode}
			<div id="new-collection">
				<div id="new-collection-input">
					<AdaptiveTextInput
						error={error}
						placeholder={placeholder}
						bind:value={inputText}
						on:blur={() => (error = false)}
						on:input={() => (error = false)} />
				</div>
				<InlineButton
					on:click={() => {
						newCollectionEvent(inputText);
						inputText = '';
					}}>
					<span id="new-collection-inner-button"> {$t('global.ok')} </span>
				</InlineButton>
			</div>
		{/if}
	</div>
</div>

<style lang="scss">
	@import "../../../variables.scss";

	.container {
		display: flex;
		flex-direction: column;
		gap: 20px;

		#new-collection {
			display: flex;
			align-items: center;
			gap: 10px;

			#new-collection-input {
				font-size: 1rem;
				font-weight: 500;
				color: #6d6f76;
				width: 100px;
				height: 30px;
			}

			#new-collection-inner-button {
				font-size: 1rem;
				color: #6d6f76;
				font-weight: 500;
			}
		}

		#action {
			display: flex;
			height: 32px;
			align-items: center;

			&:not(.editmode) {
				justify-content: flex-end;
			}

			&:not(.multipleSelectMode) {
				#functions {
					display: none;
				}
			}

			&.editmode {
				justify-content: space-between;
				#multipleselect {
					display: flex;
				}
			}

			&.multipleSelectMode {
				justify-content: flex-end;
				gap: 20px;

				#multipleselect {
					order: 2;
				}

				#functions {
					order: 1;
					display: flex;
				}

				#edit-change {
					display: none;
				}
			}

			#functions {
				display: none;
				gap: 20px;

				img {
					&:hover {
						cursor: pointer;
					}
					object-fit: contain;
				}

				#multiple-edit {
					width: 26.2px;
					height: 26.2px;
				}
				#multiple-delete {
					width: 26px;
					height: 26.2px;
				}
			}

			#multipleselect {
				display: none;
				width: 73px;
				height: 31px;
				#inner {
					display: flex;
					justify-content: space-around;
					gap: 5px;
					.icon {
						display: flex;
						width: 18px;
						height: 18px;
						min-width: 18px;
						min-height: 18px;
					}

					.text {
						font-weight: 500;
						font-size: 1rem;
						color: white;
					}
				}
			}

			#edit-change {
				display: flex;
				gap: 10px;
				align-items: center;
				color: #6d6f76;
				font-weight: 500;

				&:hover {
					cursor: pointer;
				}
				img {
					width: 24px;
					height: 24px;
					object-fit: contain;
				}
			}
		}

		#collections {
			display: flex;
			flex-direction: row;
			align-items: center;
			gap: 20px;
			flex-wrap: wrap;

			.text {
				font-weight: 500;
				color: #6d6f76;
				&:hover {
					cursor: pointer;
				}
				&.selected {
					font-weight: 550;
					color: #3c68d6;
				}
			}
		}
	}

	@include DesktopSize {
		.container {
			flex-direction: row;
			justify-content: space-between;
			gap: 10px;

			:global(.-edit-button) {
				order: 3;
			}

			#new-collection {
				display: flex;
				align-items: center;
				gap: 10px;
				font-size: 1.25rem;

				#new-collection-inner-button {
					font-size: 1.25rem;
					color: #6d6f76;
					font-weight: 500;
				}
				#new-collection-input {
					font-size: 1.25rem;
					width: 200px;
					height: 40px;
				}
			}

			#action {
				order: 2;
				height: 36px;
				width: 374px;
				min-width: 374px;
				&.editmode {
					justify-content: flex-end;
					gap: 0px;
				}
				#edit-change {
					font-size: 1.25rem;
				}

				&.multipleSelectMode {
					gap: 0px;

					#multipleselect {
						order: 2;
					}

					#functions {
						margin-right: 20px;
						display: flex;
						order: 1;

						#multiple-edit {
							display: none;
						}
					}

					#edit-change {
						display: flex;
						opacity: 0.4;
					}
				}

				#multipleselect {
					width: 84px;
					height: 36px;
					margin-right: 40px;

					#inner {
						display: flex;
						justify-content: space-around;
						align-items: center;
						gap: 0px;
						.icon {
							width: 20px;
							height: 20px;
							min-width: 20px;
							min-height: 20px;
						}

						.text {
							font-size: 1.25rem;
						}
					}
				}
			}

			#collections {
				order: 1;
				flex-grow: 1;
				gap: 20px 30px;
				min-height: 36px;
				.text {
					display: flex;
					align-items: center;
					font-size: 1.25rem;
				}
			}
		}
	}

</style>
